import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, getDocs, query, where, collection, limit, addDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Event, Item } from './types';

// Create checkout session for event tickets
export async function createEventCheckoutSession(eventId: string, eventType: string, quantity: number, price: number, metadata?: Record<string, string>): Promise<string> {
  try {
    console.log(`Creating checkout session for event: ${eventId}, type: ${eventType}, quantity: ${quantity}, price: ${price}`);
    
    // Get the current user (optional now)
    const user = auth.currentUser;
    
    // First, get the product and price ID for this event
    console.log(`Retrieving product for event: ${eventId}`);
    const product = await getProduct(eventId);
    console.log(`Product retrieval result:`, product);
    
    if (!product || !product.prices || product.prices.length === 0) {
      throw new Error('Product or price not found for this event');
    }
    
    const priceId = product.prices[0].id;
    console.log(`Using price ID: ${priceId} for checkout session`);
    
    // Get Firestore
    const db = getFirestore();
    
    // Prepare metadata with or without user ID
    const checkoutMetadata = {
      ...metadata,
      eventId,
      eventType,
      userId: user ? user.uid : 'guest'
    };

    // For authenticated users, store in their specific collection
    if (user) {
      // Prepare checkout session data
      const checkoutSessionData = {
        price: priceId,
        quantity: quantity,
        mode: 'payment', // Explicitly set mode to "payment" for one-time purchases
        success_url: window.location.origin + '/portal/events/registration-success?event=' + eventId,
        cancel_url: window.location.origin + '/portal/events/registration?event=' + eventId,
        metadata: checkoutMetadata,
      };
      
      console.log(`Creating checkout session with data for authenticated user:`, checkoutSessionData);
      
      // Create a new checkout session document
      const checkoutSessionRef = await addDoc(
        collection(db, 'customers', user.uid, 'checkout_sessions'),
        checkoutSessionData
      );
      
      console.log(`Checkout session document created: ${checkoutSessionRef.id}`);
      
      // Wait for the CheckoutSession to get attached by the extension
      return new Promise((resolve, reject) => {
        console.log(`Waiting for checkout session update: ${checkoutSessionRef.id}`);
        
        const unsubscribe = onSnapshot(checkoutSessionRef, async (snap) => {
          const data = snap.data();
          console.log('Checkout session updated:', data);

          const { sessionId, error: sessionError, url } = data || {};

          if (sessionError) {
            console.error('Session creation error:', sessionError);
            unsubscribe();
            reject(new Error(`Checkout session error: ${sessionError.message}`));
            return;
          }

          if (url) {
            console.log(`Received checkout URL: ${url}`);
            unsubscribe();
            resolve(url);
            return;
          }
          
          if (sessionId) {
            console.log(`Received sessionId but no URL - constructing URL for: ${sessionId}`);
            unsubscribe();
            resolve(`https://checkout.stripe.com/pay/${sessionId}`);
            return;
          }
          
          console.log('Checkout session update did not contain url or sessionId yet');
        }, 
        (error) => {
          console.error('Error listening to checkout session:', error);
          reject(error);
        });
        
        // Safety timeout after 30 seconds
        setTimeout(() => {
          console.log(`Checkout session timed out after 30 seconds: ${checkoutSessionRef.id}`);
          unsubscribe();
          reject(new Error('Checkout session creation timed out'));
        }, 30000);
      });
    } else {
      // For guest users, call the Cloud Function directly
      console.log(`Creating checkout session through Cloud Function for guest user`);
      
      // Parse the URLs from the current page
      const currentUrl = window.location.href;
      const isPortal = currentUrl.includes('/portal/');
      
      // Determine the success and cancel URLs
      const baseUrl = window.location.origin;
      const successUrl = isPortal 
        ? `${baseUrl}/portal/events/registration-success?event=${eventId}`
        : `${baseUrl}/events/registration-success?event=${eventId}`;
      
      const cancelUrl = isPortal
        ? `${baseUrl}/portal/events/registration?event=${eventId}`
        : `${baseUrl}/events/registration?event=${eventId}`;
      
      // Call the Firebase Function directly for guest checkout
      const functions = getFunctions();
      const createCheckoutFunc = httpsCallable(functions, 'createCheckoutSession');
      
      const result = await createCheckoutFunc({
        priceId: priceId,
        quantity: quantity,
        successUrl: successUrl,
        cancelUrl: cancelUrl,
        metadata: checkoutMetadata
      });
      
      const sessionData = result.data as { url: string, sessionId: string };
      console.log('Guest checkout session created:', sessionData);
      
      if (!sessionData || !sessionData.url) {
        throw new Error('Failed to create guest checkout session');
      }
      
      return sessionData.url;
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw new Error(`Failed to create checkout session: ${error instanceof Error ? error.message : String(error)}`);
  }
}

// Function to create or update Stripe products for events
// Call this after creating or updating an event
export const createOrUpdateStripeProduct = async (eventId: string): Promise<void> => {
  try {
    // Get the event data
    const db = getFirestore();
    const eventDoc = await getDoc(doc(db, "events", eventId));
    
    if (!eventDoc.exists()) {
      console.error("Event not found:", eventId);
      return;
    }
    
    const event = { id: eventId, ...eventDoc.data() } as Event;
    
    // Only create products for visible events with a price
    if (event.status === 'hidden' || parseFloat(event.price) <= 0) {
      return;
    }
    
    // Create or update the Stripe product using Firebase Functions
    const functions = getFunctions();
    const createStripeProduct = httpsCallable(functions, 'createStripeProduct');
    
    // Prepare the data for the function call
    const data = {
      name: event.title,
      description: event.description,
      eventId: event.id,
      priceInCents: parseFloat(event.price) * 100,
      currency: 'aud',
      active: event.status === 'open' || event.status === 'closed',
      type: 'one_time', // Explicitly mark as one-time payment, not subscription
      product_data: {
        metadata: {
          type: 'event_ticket',
          payment_type: 'one_time'
        }
      }
    };
    
    // Call the Firebase Function
    const result = await createStripeProduct(data);
    console.log('Stripe product created/updated:', result);
    
    // Update the event with the Stripe product ID
    if (result && result.data) {
      const { productId } = result.data as { productId: string };
      
      // Update the event with the Stripe product ID
      await updateDoc(doc(db, "events", eventId), {
        stripeProductId: productId,
        updatedAt: Date.now()
      });
      
      console.log(`Updated event ${eventId} with Stripe product ID: ${productId}`);
    }
    
  } catch (error) {
    console.error("Error creating/updating Stripe product for event:", error);
    throw error;
  }
};

// Function to create or update Stripe products for items
// Call this after creating or updating an item
export const createOrUpdateStripeProductForItem = async (itemId: string): Promise<void> => {
  try {
    // Get the item data
    const db = getFirestore();
    const itemDoc = await getDoc(doc(db, "items", itemId));
    
    if (!itemDoc.exists()) {
      console.error("Item not found:", itemId);
      return;
    }
    
    const item = { id: itemId, ...itemDoc.data() } as Item;
    
    // Only create products for visible items with a price
    if (item.hidden || parseFloat(item.price) <= 0) {
      return;
    }
    
    // Create or update the Stripe product using Firebase Functions
    const functions = getFunctions();
    const createStripeProduct = httpsCallable(functions, 'createStripeProduct');
    
    // Prepare the data for the function call
    const data = {
      name: item.name,
      description: item.description,
      eventId: item.id, // Using the item ID in the eventId field since the function expects this parameter
      priceInCents: parseFloat(item.price) * 100,
      currency: 'aud',
      active: !item.hidden,
      type: 'one_time',
      product_data: {
        metadata: {
          type: 'item',
          payment_type: 'one_time'
        }
      }
    };
    
    // Call the Firebase Function
    const result = await createStripeProduct(data);
    console.log('Stripe product created/updated for item:', result);
    
    // Update the item with the Stripe product ID
    if (result && result.data) {
      const { productId } = result.data as { productId: string };
      
      // Update the item with the Stripe product ID
      await updateDoc(doc(db, "items", itemId), {
        stripeProductId: productId,
        updatedAt: Date.now()
      });
    }
    
  } catch (error) {
    console.error("Error creating/updating Stripe product for item:", error);
    throw error;
  }
};

// Get all products and prices
export async function getProducts() {
  try {
    const db = getFirestore();
    const productsRef = collection(db, "products");
    const productsSnapshot = await getDocs(productsRef);
    
    // Process each product and add its prices
    const products = [];
    
    for (const productDoc of productsSnapshot.docs) {
      const productId = productDoc.id;
      const productData = productDoc.data();
      
      // Get prices for this product
      const pricesSnapshot = await getDocs(
        query(
          collection(db, "products", productId, "prices"),
          where("active", "==", true)
        )
      );
      
      const prices = pricesSnapshot.docs.map(priceDoc => ({
        id: priceDoc.id,
        ...priceDoc.data()
      }));
      
      products.push({
        id: productId,
        ...productData,
        prices
      });
    }
    
    return products;
  } catch (error) {
    console.error("Error getting products:", error);
    return [];
  }
}

// Get a specific product by ID
export async function getProduct(productId: string) {
  try {
    const db = getFirestore();
    console.log(`Looking for product with ID or eventId: ${productId}`);
    
    // First try to find the product by ID (direct match)
    const productDoc = await getDoc(doc(db, "products", productId));
    
    if (productDoc.exists()) {
      console.log(`Found product directly by ID: ${productId}`, productDoc.data());
      
      // Get the first active price for this product
      const pricesSnapshot = await getDocs(
        query(
          collection(db, "products", productId, "prices"),
          where("active", "==", true),
          limit(1)
        )
      );
      
      if (!pricesSnapshot.empty) {
        const priceDoc = pricesSnapshot.docs[0];
        console.log(`Found price for product: ${productId}`, priceDoc.data());
        
        return {
          id: productId,
          ...productDoc.data(),
          prices: [{ id: priceDoc.id, ...priceDoc.data() }]
        };
      } else {
        console.log(`No active prices found for product: ${productId}`);
      }
      
      // Return product even if no prices found
      return {
        id: productId,
        ...productDoc.data(),
        prices: []
      };
    }
    
    // If product not found by ID, try to find by eventId in metadata
    console.log(`Product not found directly, trying to find by eventId: ${productId}`);
    const productsRef = collection(db, "products");
    const eventProductQuery = query(productsRef, where("eventId", "==", productId));
    const eventProductSnapshot = await getDocs(eventProductQuery);
    
    if (!eventProductSnapshot.empty) {
      const productDoc = eventProductSnapshot.docs[0];
      const productId = productDoc.id;
      console.log(`Found product with eventId: ${productId}`, productDoc.data());
      
      // Get the prices for this product
      const pricesSnapshot = await getDocs(
        query(
          collection(db, "products", productId, "prices"),
          where("active", "==", true),
          limit(1)
        )
      );
      
      if (!pricesSnapshot.empty) {
        const priceDoc = pricesSnapshot.docs[0];
        console.log(`Found price for product with eventId: ${productId}`, priceDoc.data());
        
        return {
          id: productId,
          ...productDoc.data(),
          prices: [{ id: priceDoc.id, ...priceDoc.data() }]
        };
      } else {
        console.log(`No active prices found for product with eventId: ${productId}`);
      }
      
      // Return product even if no prices found
      return {
        id: productId,
        ...productDoc.data(),
        prices: []
      };
    }
    
    console.log(`No product found for ID or eventId: ${productId}`);
    return null; // Return null if product not found
  } catch (error) {
    console.error(`Error getting product ${productId}:`, error);
    return null;
  }
}

// Get user subscriptions
export async function getUserSubscriptions() {
  try {
    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      return [];
    }
    
    const db = getFirestore();
    // Check if the user has a customers document
    const customerDoc = await getDoc(doc(db, 'customers', user.uid));
    if (!customerDoc.exists()) {
      return [];
    }
    
    // Get subscriptions from the customers document
    const subscriptions = customerDoc.data()?.subscriptions || [];
    return Object.values(subscriptions);
  } catch (error) {
    console.error('Error getting user subscriptions:', error);
    return [];
  }
} 